import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ImageCropper.css';

const ImageCropper = ({ imageFile, onCropComplete, onCancel, aspect = 1 }) => {
  const { t } = useTranslation(['settings', 'common']);
  const [crop, setCrop] = useState({ unit: '%', width: 90, aspect });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Handle percentage-based crops
    let cropX, cropY, cropWidth, cropHeight;
    if (crop.unit === '%') {
      cropX = (crop.x / 100) * image.naturalWidth;
      cropY = (crop.y / 100) * image.naturalHeight;
      cropWidth = (crop.width / 100) * image.naturalWidth;
      cropHeight = (crop.height / 100) * image.naturalHeight;
    } else {
      cropX = crop.x * scaleX;
      cropY = crop.y * scaleY;
      cropWidth = crop.width * scaleX;
      cropHeight = crop.height * scaleY;
    }
    
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.95);
    });
  };

  const handleCropComplete = async () => {
    if (imgRef.current && completedCrop) {
      try {
        const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
        const fileName = imageFile?.name || 'profile-image.jpg';
        const croppedFile = new File([croppedImageBlob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        onCropComplete(croppedFile);
      } catch (error) {
        console.error('Error cropping image:', error);
        onCancel();
      }
    }
  };

  if (!imageSrc) {
    return null;
  }

  return (
    <div className="image-cropper-modal">
      <div className="image-cropper-content">
        <h3>{t('settings:cropImageTitle')}</h3>
        <div className="crop-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={aspect}
            circularCrop={aspect === 1}
          >
            <img
              ref={imgRef}
              alt={t('settings:cropPreviewAlt')}
              src={imageSrc}
              style={{ maxWidth: '100%', maxHeight: '400px' }}
            />
          </ReactCrop>
        </div>
        <div className="crop-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCropComplete}
            disabled={!completedCrop}
          >
            {t('settings:applyCrop')}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            {t('common:cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;

